import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { BadgePercent, CalendarDays, FileText, LogOut, PackageOpen, RotateCcw, Star } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate, money, money2 } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal } from "@/components/Reveal";

const BookingCard = ({ b, onCancel }) => (
  <div
    className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-all duration-300 hover:border-royal/40 hover:shadow-lg hover:shadow-royal/5 sm:flex-row sm:items-center"
    data-testid={`booking-card-${b.id}`}
  >
    <img src={b.listing_image} alt="" className="h-20 w-24 rounded-xl object-cover" />
    <div className="flex-1">
      <div className="flex flex-wrap items-center gap-2.5">
        <p className="font-display text-base font-bold text-ink">{b.listing_title}</p>
        <span
          className={`rounded-full px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] ${
            b.status === "confirmed" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"
          }`}
        >
          {b.status}
        </span>
      </div>
      <p className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500">
        <CalendarDays className="h-3.5 w-3.5" />
        {fmtDate(b.start_date)} – {fmtDate(b.end_date)} · {b.days} day{b.days === 1 ? "" : "s"}
        {b.dollies ? ` · ${b.dollies} ${b.dollies > 1 ? "dollies" : "dolly"}` : ""}
        {b.self_pickup ? " · self pick-up" : ""}
        {b.balance_due > 0 ? ` · ${money2(b.balance_due)} due on delivery` : ""}
        {b.damage_fees_total > 0 ? ` · ${money2(b.damage_fees_total)} return fees` : ""}
      </p>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
        Ref {b.reference}
      </p>
    </div>
    <div className="flex items-center justify-between gap-4 sm:flex-col sm:items-end">
      <p className="font-display text-lg font-extrabold text-ink">{money(b.total)}</p>
      <div className="flex items-center gap-2">
        <Link
          to={`/listing/${b.listing_id}?weeks=${Math.max(1, Math.round(b.days / 7))}&rebook=${b.id}`}
          data-testid={`rebook-button-${b.id}`}
          className="flex items-center gap-1.5 rounded-full bg-royal px-4 py-1.5 text-xs font-semibold text-white transition-colors duration-200 hover:bg-royal-dark"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Book again
        </Link>
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/bookings/${b.id}/invoice`}
          target="_blank"
          rel="noreferrer"
          data-testid={`invoice-button-${b.id}`}
          className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-1.5 text-xs font-semibold text-slate-500 transition-colors duration-200 hover:border-royal hover:text-royal"
        >
          <FileText className="h-3.5 w-3.5" /> Invoice
        </a>
        {b.status === "confirmed" && (
          <button
            onClick={() => onCancel(b.id)}
            data-testid={`cancel-booking-button-${b.id}`}
            className="rounded-full border border-slate-200 px-4 py-1.5 text-xs font-semibold text-slate-500 transition-colors duration-200 hover:border-red-300 hover:text-red-600"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  </div>
);

export default function AccountPage() {
  const { user, logout } = useAuth();
  const [bookings, setBookings] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    api.get("/bookings/my").then((r) => setBookings(r.data)).catch(() => setBookings([]));
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  if (user === null) return <main className="min-h-[60vh]" />;
  if (user === false) return <Navigate to="/auth" state={{ from: "/account" }} replace />;

  const cancel = async (id) => {
    try {
      await api.patch(`/bookings/${id}/cancel`);
      toast.success("Reservation cancelled. Deposit released.");
      load();
    } catch {
      toast.error("Could not cancel that reservation");
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const active = (bookings || []).filter((b) => b.status === "confirmed" && b.end_date >= today);
  const past = (bookings || []).filter((b) => !(b.status === "confirmed" && b.end_date >= today));

  const signOut = async () => {
    await logout();
    toast.success("Signed out. Your totes will be waiting.");
    navigate("/");
  };

  return (
    <main className="bg-paper pb-28" data-testid="account-page">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 px-4 py-14 sm:px-8 lg:px-16">
          <div className="flex items-center gap-5">
            <span className="grid h-16 w-16 place-items-center rounded-2xl bg-royal font-display text-2xl font-extrabold text-white shadow-lg shadow-royal/25">
              {(user.name || user.email)[0].toUpperCase()}
            </span>
            <div>
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-royal">
                Oregon member
              </p>
              <h1 className="mt-1 font-display text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
                {user.name}
              </h1>
              <p className="text-sm text-slate-500">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/reviews"
              data-testid="account-review-button"
              className="flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 transition-colors duration-200 hover:border-royal hover:text-royal"
            >
              <Star className="h-4 w-4" /> Leave a review
            </Link>
            <button
              onClick={signOut}
              data-testid="account-signout-button"
              className="flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 transition-colors duration-200 hover:border-red-300 hover:text-red-600"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
        {user.returning_discount && (
          <div className="mx-auto max-w-7xl px-4 pb-10 sm:px-8 lg:px-16" data-testid="loyalty-banner">
            <div className="flex items-center gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
              <img
                src="https://customer-assets-lqy194kg.emergentagent.net/job_lease-portal-36/artifacts/qxlzg4uc_happy.jfif"
                alt="A happy returning customer"
                data-testid="loyalty-banner-image"
                className="h-16 w-16 shrink-0 rounded-2xl border-2 border-white object-cover shadow-md sm:h-20 sm:w-20"
              />
              <div>
                <p className="font-display text-base font-bold text-emerald-800">
                  Welcome-back pricing is on
                </p>
                <p className="text-sm text-emerald-700">
                  You're a returning customer — 10% off is applied automatically at checkout on every future rental. No code needed.
                </p>
              </div>
              <BadgePercent className="ml-auto h-8 w-8 shrink-0 text-emerald-600" />
            </div>
          </div>
        )}
      </section>

      <section className="mx-auto max-w-7xl px-4 pt-12 sm:px-8 lg:px-16">
        <Reveal>
          <h2 className="font-display text-xl font-bold text-ink">Active rentals</h2>
          <div className="mt-5 space-y-4">
            {bookings === null && <div className="h-28 animate-pulse rounded-2xl bg-slate-200/60" />}
            {bookings !== null && active.length === 0 && (
              <div className="flex items-center gap-4 rounded-2xl border border-dashed border-slate-300 bg-white p-8">
                <PackageOpen className="h-6 w-6 text-slate-400" />
                <p className="text-sm text-slate-500">
                  No active rentals. Your next move is one reservation away.
                </p>
              </div>
            )}
            {active.map((b) => <BookingCard key={b.id} b={b} onCancel={cancel} />)}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-14 font-display text-xl font-bold text-ink">History</h2>
          <div className="mt-5 space-y-4">
            {past.length === 0 && bookings !== null && (
              <p className="text-sm text-slate-500">Nothing in the archive yet.</p>
            )}
            {past.map((b) => <BookingCard key={b.id} b={b} onCancel={cancel} />)}
          </div>
        </Reveal>
      </section>
    </main>
  );
}
